import { useState } from "react";
import { Link } from "react-router-dom";
import { Smartphone, Bell, ChevronDown, CheckCircle2, Share, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isAndroid, isIOS, isStandalone } from "@/lib/push";
import { Step, IOS_STEPS, ANDROID_STEPS, openShareSheet } from "@/pages/PushGuide";

const BENEFITS = ["Push-Mitteilung bei jedem neuen Video", "Nichts mehr verpassen – Erinnerung an deinen Login-Bonus", "Überall informiert – mit einem Tipp in der App"];

const Guide = () => {
  const [tab, setTab] = useState(isAndroid() ? "android" : "ios");
  const steps = tab === "ios" ? IOS_STEPS : ANDROID_STEPS;
  return (
    <div className="space-y-3" data-testid="homescreen-guide">
      <div className="flex gap-2">
        {[["ios", "iPhone"], ["android", "Android"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`homescreen-tab-${k}`}
            className={`px-4 py-1.5 rounded-full text-xs font-bold transition-colors ${tab === k ? "bg-rose-500 text-white" : "bg-white border border-pink-100 text-slate-600"}`}>{l}</button>
        ))}
      </div>
      <ol className="space-y-2">{steps.map((s, i) => <Step key={s.title} n={i + 1} {...s} />)}</ol>
      {tab === "ios" && isIOS() && (
        <button onClick={openShareSheet} data-testid="homescreen-share-btn" className="inline-flex items-center gap-2 px-4 h-10 rounded-full bg-slate-900 text-white text-xs font-bold">
          <Share className="w-4 h-4" /> Teilen-Menü direkt öffnen
        </button>
      )}
      <p className="text-xs text-slate-500">Danach in der App unter <Link to="/push" className="font-semibold text-rose-600 underline">Push</Link> die Benachrichtigungen aktivieren.</p>
    </div>
  );
};

export const HomeScreenHint = () => {
  const { user, setUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!user || user.homescreen_confirmed || isStandalone()) return null;

  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/profile/homescreen-confirmed");
      setUser(data);
      toast.success("Super! Vergiss nicht, unter „Push“ die Benachrichtigungen zu aktivieren 🔔");
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="mb-6 rounded-3xl border border-rose-200 bg-white overflow-hidden fade-up" data-testid="homescreen-hint">
      <div className="p-5 sm:p-6 flex flex-col sm:flex-row gap-4 sm:items-start">
        <div className="w-12 h-12 rounded-2xl pink-gradient text-white flex items-center justify-center shrink-0"><Smartphone className="w-6 h-6" /></div>
        <div className="flex-1 min-w-0 space-y-2">
          <p className="font-bold text-lg">Tipp: Hol dir itzPlay auf deinen Home-Bildschirm</p>
          <p className="text-sm text-slate-600">Es lohnt sich, die App zum Home-Bildschirm hinzuzufügen – so bekommst du immer eine Mitteilung, verpasst nichts und bist überall informiert.</p>
          <ul className="flex flex-wrap gap-2 pt-1">
            {BENEFITS.map((b) => <li key={b} className="text-xs font-semibold px-3 py-1 rounded-full bg-rose-50 text-rose-700 flex items-center gap-1"><Bell className="w-3 h-3" /> {b}</li>)}
          </ul>
        </div>
      </div>
      <div className="px-5 sm:px-6 pb-5 sm:pb-6 space-y-4">
        <button onClick={() => setOpen(!open)} data-testid="homescreen-guide-toggle" className="flex items-center gap-1 text-sm font-bold text-rose-600">
          {open ? "Anleitung ausblenden" : "So geht's – Anleitung anzeigen"} <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
        {open && <Guide />}
        <button onClick={confirm} disabled={busy} data-testid="homescreen-confirm-btn"
          className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 h-11 rounded-full pink-gradient text-white font-bold text-sm pink-glow disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Ich habe die App zum Home-Bildschirm hinzugefügt
        </button>
      </div>
    </div>
  );
};
